import { createContext, useContext } from "react"

// =============================================================================
// Context
// =============================================================================

export const NavigationAccountType = {
  Free: "free",
  Paid: "paid"
} as const

export type NavigationAccountType =
  (typeof NavigationAccountType)[keyof typeof NavigationAccountType]

export const NavigationServiceLevel = {
  CreatorFree: "creator_free",
  Creator: "creator",
  CreatorPro: "creator_pro",
  NewsletterFree: "newsletter_free",
  NewsletterPaid: "newsletter_paid"
} as const

export type NavigationServiceLevel =
  (typeof NavigationServiceLevel)[keyof typeof NavigationServiceLevel]

export type NavigationContextValue = {
  /** Whether the sidebar is currently collapsed */
  collapsed: boolean
  /** Setter to update the collapsed state */
  setCollapsed: (collapsed: boolean) => void
  /** Current pathname (e.g. `/dashboard`) — used by items to derive their own active state */
  currentPath: string
  /** Whether the navigation is rendered in a mobile layout (dock + full-width sidebar) */
  mobile: boolean
  /** Whether the navigation is hidden entirely, independently of `collapsed` */
  hidden: boolean
  /**
   * Whether the current account is on a paid plan. Paid accounts hide the
   * `"paid"` badge icon and its tooltip callout on nav rows.
   */
  accountType: NavigationAccountType
  /**
   * The specific service level of the current account. Used in combination with `accountType` to
   * gate the `"pro"` badge. Mirrors `window.app.user.access_level.service_level`.
   */
  serviceLevel?: NavigationServiceLevel
}

export const NavigationContext = createContext<NavigationContextValue | null>(
  null
)

export function useNavigationContext(): NavigationContextValue {
  const context = useContext(NavigationContext)
  if (!context)
    throw new Error("useNavigationContext must be used within a Navigation")
  return context
}
